import classnames from 'classnames';
import { useAppSelector } from 'hooks/useAppSelector';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { useState } from 'react';
import { type IntlShape, useIntl } from 'react-intl';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { BasketOptimizerResultRow } from 'state/advisory/basket-optimizer/model';
import { getResultRows, isAdvisoryProcessing } from 'state/advisory/basket-optimizer/selectors';
import { loadFormWithAdvisoryResultThunk } from 'state/advisory/thunk';
import { now } from 'utils/core/now';
import { downloadCsv } from 'utils/documents/downloadDocument';

const e2e = 'basket-optimizer';
const useResult = (tabId: string) => {
  const dispatch = useThunkDispatch();
  return {
    isOptimizing: useAppSelector(state => isAdvisoryProcessing(state, tabId)),
    baskets: useAppSelector(state => getResultRows(state, tabId)),
    onResultSelected(row: BasketOptimizerResultRow) {
      dispatch(loadFormWithAdvisoryResultThunk(row.underlyingIds));
      logAnalytics.trackEvent('SG Advisory', 'Quote from optimized priced');
    },
  };
};
interface Props {
  tabId: string;
}

export function BasketResults({ tabId }: Props) {
  const intl = useIntl();
  const csvName = intl.formatMessage({ id: 'basketOptimizer.optimiseMyUnderlyingUniverse' });
  const [reversed, setReversed] = useState(false);
  const [hasStartedExport, setHasStartedExport] = useState(false);
  const [csvFileName, setCsvFileName] = useState<string | undefined>();
  const { baskets, isOptimizing, onResultSelected } = useResult(tabId);
  const sortBy = () => {
    setReversed(!reversed);
  };

  const results = baskets ? (reversed ? baskets.reverse() : baskets) : [];

  const exportToCSV = (rows: BasketOptimizerResultRow[]) => {
    function csvFileName(name: string, date: Date, intl: IntlShape) {
      const timestamp = intl.formatDate(date, {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
      });

      let fileName = `${timestamp}_${name}`;

      if (!name.endsWith('.csv')) {
        fileName += '.csv';
      }

      return fileName;
    }

    if (!hasStartedExport) {
      setHasStartedExport(true);
      const header = 'Underlyings,Result';
      const csv = rows.reduce((prev: string, r: BasketOptimizerResultRow) => {
        return `${prev}\r\n"${r.underlyingIds.join(', ')}",${r.price}`;
      }, header);

      const fileName = csvFileName(csvName, now(), intl).replace(/(\/|:|\\)/g, '_');

      downloadCsv(csv, fileName);

      setHasStartedExport(false);
      setCsvFileName(fileName);
    }
  };

  return (
    !isOptimizing && (
      <div className="mt-2">
        <div data-e2e={`${e2e}-results`}>
          {results.length > 0 && (
            <div className="export-section" data-e2e="export-csv">
              {hasStartedExport ? (
                <>
                  <div className="spinner-border mb-2" role="status" />
                  Loading...
                </>
              ) : (
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <div className="fw-bold mb-0">Results</div>
                  <div>
                    <button
                      className="btn btn-flat-secondary"
                      onClick={sortBy}
                      data-e2e={`${e2e}-results-sort`}
                      data-toggle="tooltip"
                      data-placement="left"
                      title={intl.formatMessage({
                        id: 'sidePanel.tabs.advisory.sort',
                      })}
                    >
                      <i className="icon">sort</i>
                    </button>
                    <button
                      className="btn btn-flat-secondary"
                      data-e2e={`${e2e}-results-export`}
                      onClick={() => exportToCSV(results)}
                      data-toggle="tooltip"
                      data-placement="top"
                      title={intl.formatMessage({
                        id: 'sidePanel.tabs.advisory.exportToCsv',
                      })}
                    >
                      <i className="icon">save_alt</i>
                    </button>

                    {csvFileName && (
                      <input type="hidden" data-e2e={`${e2e}-results-export-filename`} value={csvFileName} />
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          {results.slice(0, 10).map((result: BasketOptimizerResultRow, index: number) => (
            <div
              className="clickable bg-lvl2 d-flex align-items-center p-2 text-secondary fw-medium mb-1"
              data-e2e={`${e2e}-result-tr`}
              key={index}
              onClick={() => {
                onResultSelected(result);
              }}
            >
              <span key={index} className={classnames('flex-grow-0', { 'text-secondary fw-medium': index === 0 })}>
                {result.underlyingIds.join(', ')}
              </span>
              <span
                className={classnames('flex-grow-1 text-end me-2', {
                  'text-success fw-medium': index === 0,
                })}
              >
                {isNaN(result.price) ? result.price : Number(result.price).toFixed(4)}
              </span>
              <button className="flex-grow-0 btn btn-flat-secondary btn-icon">
                <em className="icon">library_add</em>
              </button>
            </div>
          ))}
        </div>
      </div>
    )
  );
}
